import { useEffect, useState } from "react";
import { Footer } from "../../layout/footer";
import { PrintHeader } from "../../layout/PrintHeader";
import { reportCRUD } from "../../actions/report";
import { BoundaryCascade } from "./BoundaryCascade";
import { Loading } from "../constant";

const nf = (n) => (n ?? 0).toLocaleString();

// One bordered report table (SR + label + M + F + Total).
const ReportTable = ({ title, firstCol, rows }) => (
  <div className="mb-4">
    <h6 className="text-primary fw-semibold mb-2">{title}</h6>
    <div className="table-responsive">
      <table className="table table-bordered align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th style={{ width: 70 }}>SR No.</th>
            <th>{firstCol}</th>
            <th className="text-end">Male</th>
            <th className="text-end">Female</th>
            <th className="text-end">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td>{r.label}</td>
              <td className="text-end">{nf(r.male)}</td>
              <td className="text-end">{nf(r.female)}</td>
              <td className="text-end fw-semibold">{nf(r.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const VitalReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [areaName, setAreaName] = useState("Zanzibar");

  const load = (boundaryId, name) => {
    setLoading(true);
    setAreaName(name || "Zanzibar");
    reportCRUD
      .loadVitalReport(boundaryId)
      .then((resp) => setData(resp.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(null, "Zanzibar");
  }, []);

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          {/* Header */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0">
                  <i className="ri-file-list-3-line align-bottom me-1 text-primary"></i>
                  Vital Statistical Report
                </h4>
                <div className="page-title-right">
                  <ol className="breadcrumb m-0">
                    <li className="breadcrumb-item"><a href="#">Reports</a></li>
                    <li className="breadcrumb-item active">Vital Statistical</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Location filter (Shehia / District / Region) */}
          <div className="card mb-3">
            <div className="card-body">
              <BoundaryCascade onChange={(id, name) => load(id, name)} />
            </div>
          </div>

          {loading && <div className="row"><Loading /></div>}

          {!loading && data && (
            <div className="card">
              <div className="card-body">
                <PrintHeader subtitle={`Vital Statistical Report — ${areaName}`} />

                {/* 1. Population by age */}
                <ReportTable
                  title="Population"
                  firstCol="Age Range"
                  rows={(data.population || []).map((r) => ({
                    label: r.age_range, male: r.male, female: r.female, total: r.total,
                  }))}
                />

                {/* 2. Household */}
                <ReportTable
                  title="Household"
                  firstCol="Household"
                  rows={[{
                    label: `Households (${nf(data.households?.count)})`,
                    male: data.households?.head_male,
                    female: data.households?.head_female,
                    total: (data.households?.head_male || 0) + (data.households?.head_female || 0),
                  }]}
                />

                {/* 3. Members in area */}
                <ReportTable
                  title={`Household Members in ${areaName}`}
                  firstCol="Members"
                  rows={[{
                    label: "All members",
                    male: data.totals?.male,
                    female: data.totals?.female,
                    total: data.totals?.total,
                  }]}
                />

                {/* 4. Migration — Residence types */}
                <ReportTable
                  title="Migration — Types of Residence"
                  firstCol="Type of Residence"
                  rows={(data.residences || []).map((r) => ({
                    label: r.residence_type, male: r.male, female: r.female, total: r.total,
                  }))}
                />

                {/* 5. Migration movement (immigrant / emigrant) */}
                <div className="mb-2">
                  <h6 className="text-primary fw-semibold mb-2">Migration — Movement</h6>
                  {data.migration?.has_data ? (
                    <table className="table table-bordered align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Immigrant within District</th>
                          <th>Emigrant out of District</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{nf(data.migration.immigrant_within_district)}</td>
                          <td>{nf(data.migration.emigrant_out_of_district)}</td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <div className="alert alert-info mb-0">
                      <i className="ri-information-line me-1"></i>
                      Migration movement data (incoming / emigration dates) has not been
                      captured yet, so this table is empty.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!loading && !data && (
            <div className="alert alert-warning">Failed to load report.</div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};
